import { useState } from 'react';
import { Card, ErrorMessage, Field, Loading, Toggle, useAsync, useToast } from '../../components/ui';
import { IconAlert, IconDownload } from '../../components/icons';
import { api, ApiError, downloadFile } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Export und Zurücksetzen der Organisation (Kap. 17). Das Löschen gibt es nur im Branch `Multi-Tenant`.
 *
 * Nur für Superadmins, und bewusst in dieser Reihenfolge aufgebaut: Oben der
 * folgenlose Export – daran lässt sich prüfen, dass das Archiv entsteht und
 * lesbar ist, bevor es ernst wird. Darunter das Zurücksetzen. Wer nur
 * schauen wollte, muss an nichts Gefährlichem vorbei.
 */
export function TenantDangerZone({ tenantId, tenantName }: { tenantId: number; tenantName: string }) {
  const { notify } = useToast();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Administratoren dürfen nur exportieren; Zurücksetzen bleibt Superadmins vorbehalten.
  const { user } = useAuth();
  const darfReset = Boolean(user?.isSuperadmin);
  const gruppen = useAsync(
    () => (darfReset ? api.get(`/api/tenants/${tenantId}/reset-groups`) : Promise.resolve(null)),
    [tenantId, darfReset],
  );

  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [katalogeNeu, setKatalogeNeu] = useState(true);
  const [resetName, setResetName] = useState('');

  const alleGruppen: any[] = gruppen.data?.groups ?? [];
  /** Die Gruppen bauen aufeinander auf – auswählen heißt: alles davor mit. */
  const wirklichBetroffen = (() => {
    const höchste = alleGruppen.reduce((max, group, index) => (gewaehlt.includes(group.key) ? index : max), -1);
    return höchste < 0 ? [] : alleGruppen.slice(0, höchste + 1).map((g: any) => g.key);
  })();

  const lade = async (datei: string) => {
    try {
      await downloadFile(`/api/storage/backup/${encodeURIComponent(datei)}`, datei, undefined, true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'cancelled') return;
      notify('Herunterladen fehlgeschlagen – das Archiv liegt aber auf dem Server.', 'error');
    }
  };

  const exportieren = async () => {
    setBusy('export');
    setError(null);
    try {
      const result = await api.postConfirmed(`/api/tenants/${tenantId}/export`, undefined, true);
      notify(`Export erzeugt: ${result.totalRows} Zeilen, ${result.files} Dateien.`, 'success');
      await lade(result.file);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  };

  const zuruecksetzen = async () => {
    setBusy('reset');
    setError(null);
    try {
      const result = await api.postConfirmed(`/api/tenants/${tenantId}/reset`, {
        confirmName: resetName,
        groups: wirklichBetroffen,
        seedCatalogs: katalogeNeu,
      }, true);
      notify(`${result.totalRows} Zeilen zurückgesetzt.`, 'success');
      await lade(result.export.file);
      setResetName('');
      setGewaehlt([]);
      gruppen.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  };

  if (gruppen.loading) return <Loading />;

  const gesamtZeilen = alleGruppen.reduce((summe, group) => summe + Number(group.rows ?? 0), 0);

  return (
    <div className="stack">
      <ErrorMessage error={error} />

      <Card title="Daten exportieren" subtitle="Ohne jede Folge – ändert nichts">
        <div className="stack" style={{ gap: 10 }}>
          <p className="small subtle" style={{ margin: 0 }}>
            Erzeugt ein ZIP mit allen {darfReset ? `${gesamtZeilen} ` : ''}Zeilen der Organisation: je Tabelle eine
            JSON- und eine CSV-Datei, dazu Fotos, Berichte und Briefbogen als echte Dateien. Das Archiv
            wird auf dem Server abgelegt und heruntergeladen. Der Export verlangt Ihr Passwort und den Code der
            Authenticator-App.
          </p>
          <div>
            <button className="btn primary" disabled={busy !== null} onClick={exportieren}>
              <IconDownload width={14} height={14} /> {busy === 'export' ? 'Wird erzeugt …' : 'Export erzeugen'}
            </button>
          </div>
        </div>
      </Card>

      {darfReset && <Card
        title="Organisation zurücksetzen"
        subtitle="Die Organisation bleibt bestehen, gewählte Datengruppen werden geleert"
      >
        <div className="stack">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 40 }} />
                  <th>Datengruppe</th>
                  <th className="num">Zeilen</th>
                  <th>Enthält</th>
                </tr>
              </thead>
              <tbody>
                {alleGruppen.map((group: any) => {
                  const betroffen = wirklichBetroffen.includes(group.key);
                  const nurWegenAbhaengigkeit = betroffen && !gewaehlt.includes(group.key);
                  return (
                    <tr key={group.key} style={betroffen ? { background: 'var(--danger-soft, transparent)' } : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={betroffen}
                          onChange={(event) =>
                            setGewaehlt(
                              event.target.checked
                                ? [...gewaehlt, group.key]
                                : gewaehlt.filter((k) => k !== group.key),
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong>{group.label}</strong>
                        {nurWegenAbhaengigkeit && (
                          <div className="small subtle">
                            muss mit – spätere Gruppen bauen darauf auf
                          </div>
                        )}
                      </td>
                      <td className="num">{group.rows}</td>
                      <td className="small subtle">{group.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {wirklichBetroffen.includes('konfiguration') && (
            <Toggle
              checked={katalogeNeu}
              onChange={setKatalogeNeu}
              label="Kataloge anschließend auf den Auslieferungszustand setzen"
            />
          )}
          {wirklichBetroffen.includes('konfiguration') && !katalogeNeu && (
            <div className="alert warning">
              <IconAlert width={16} height={16} />
              <span>
                Ohne Kritikalitätsstufen und mindestens einen Meldekanal lässt sich anschließend kein
                Auftrag mehr anlegen.
              </span>
            </div>
          )}
          {wirklichBetroffen.includes('benutzer') && (
            <div className="alert warning">
              <IconAlert width={16} height={16} />
              <span>
                Alle Konten werden gelöscht. Danach ist die Anwendung nur noch für Superadmins erreichbar.
              </span>
            </div>
          )}

          {wirklichBetroffen.length > 0 && (
            <Field
              label={`Zur Bestätigung „${tenantName}“ eingeben`}
              hint="Vor dem Zurücksetzen wird automatisch ein vollständiger Export erzeugt."
            >
              <input value={resetName} onChange={(event) => setResetName(event.target.value)} />
            </Field>
          )}

          <div>
            <button
              className="btn danger"
              disabled={busy !== null || wirklichBetroffen.length === 0 || resetName.trim() !== tenantName}
              onClick={zuruecksetzen}
            >
              {busy === 'reset' ? 'Wird zurückgesetzt …' : `${wirklichBetroffen.length} Gruppe(n) zurücksetzen`}
            </button>
          </div>
        </div>
      </Card>}
    </div>
  );
}
